import { useTranslation } from 'react-i18next';
import classes from './SpacerWidget.module.css';
import { componentsTestIds } from '@components/testids';

/**
 * TV1-2291 (SDD §5 · t21) — the deliberate gap. The THIRD dataless widget.
 *
 * ## THE SPACER IS LOAD-BEARING, NOT DECORATIVE
 *
 * This is the reason it exists, and it is the reason it must not be "cleaned up"
 * as an empty widget by a future reader: **vertical compaction (t10) closes gaps
 * automatically.** Once every widget is pulled up to the first free row above it,
 * an empty row cannot survive a resolve — so "leave some air between the KPI row
 * and the charts below" becomes UNEXPRESSIBLE unless something occupies those
 * cells. A spacer is that something: it takes up its rectangle, keeps compaction
 * from eating the gap, and draws nothing.
 *
 * Delete it and the only remaining way to space a report out is to give a real
 * widget a height it does not need, which is worse in every direction (it lies
 * about the widget, and it breaks the moment the widget is resized).
 *
 * ## Dataless
 *
 * No cube, no measures, no Cube query, no fetch. It is a member of
 * `DATALESS_WIDGET_TYPES`, so `WidgetLayoutComponent.getWidgetData` short-circuits
 * to the `DATALESS_WIDGET_DATA` sentinel and never calls `cubeApi.load`. Its
 * domain schema (`UiWidgetSpacerSchema`) extends `UiWidgetBaseSchema`, not the
 * data base, for the same reason.
 *
 * ## Nothing for a reader, a hint for an author
 *
 * In VIEW mode the widget renders an empty box: a reader must see a gap, not a
 * placeholder explaining a gap. In EDIT mode that would make the tile invisible
 * and un-findable, so a subtle dashed label appears — the same call the prototype
 * makes (`prototype-b-grid.html`, `.canvas.editing .spacerw span`).
 *
 * `editMode` is threaded from `WidgetLayoutEditor` (which IS edit mode) through
 * `WebWidget`'s render context. The viewer passes nothing and the default is
 * `false`, so the reader-facing behaviour is the one you get by forgetting.
 */

export type SpacerWidgetProps = {
  /** `true` only when rendered by `WidgetLayoutEditor`. Readers never see the hint. */
  editMode?: boolean;
};

export const SpacerWidget = ({ editMode = false }: SpacerWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  if ( !editMode ) {
    // Occupies its cells; renders no content. `aria-hidden` because a gap is not
    // something a screen reader should announce.
    return (
      <div
        className={classes.container}
        data-testid={componentsTestIds.spacerWidget}
        aria-hidden="true"
      />
    );
  }

  return (
    <div className={classes.container} data-testid={componentsTestIds.spacerWidget}>
      <span className={classes.placeholder} data-testid={componentsTestIds.spacerWidgetPlaceholder}>
        {t( 'widget_spacer_placeholder', 'Spacer' )}
      </span>
    </div>
  );
};
